/* SCREEN — DASHBOARD (run cards + scan history). Exact port of code.txt Dashboard.
   Card internals use a flex spacer so "Start Analysis"/"Try demo" sit at the SAME
   vertical height in both cards regardless of paragraph or note length. All actions
   give immediate busy feedback. */

import { Card, Pill, SectionTitle, BtnPrimary, BtnNeutral, T, MONO, FONT, fmt, AioLabel, escapeHtml } from "../ui.js";
import { AppLogo } from "../ui.js";

export function renderHome(app, { user, hasDfs, scans }) {
  const u = user;
  const dfs = hasDfs;

  app.innerHTML = `
    <div style="min-height:100vh;background:${T.bg};padding:40px 24px;font-family:${FONT}">
      <div style="max-width:760px;margin:0 auto">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:28px">
          <div style="display:flex;align-items:center;gap:10px">
            ${AppLogo(30)}
            <div>
              <div style="font-size:14px;font-weight:800;letter-spacing:-0.3px"><span style="color:${T.violet}">AIO Gap</span> Finder</div>
              <div style="font-size:9.5px;color:${T.muted};font-weight:600;letter-spacing:0.5px;text-transform:uppercase">Free tool by Botpresso</div>
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:8px">
            <span style="font-family:${MONO};font-size:11px;color:${T.muted}">${escapeHtml(u.email)}</span>
            ${BtnNeutral("Account", { small: true, attrs: 'data-act="account"' })}
          </div>
        </div>

        ${SectionTitle("Start a new analysis")}
        <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px;margin-bottom:30px">

          ${Card(`
            ${Pill(T.violet, T.violetSoft, "GSC ONLY", "align-self:flex-start")}
            <div style="font-size:16.5px;font-weight:800;letter-spacing:-0.3px;margin:10px 0 6px">Run without SERP</div>
            <p style="font-size:12.5px;color:${T.sub};line-height:1.55;margin:0 0 16px">
              Uses only your Search Console exports. Estimates AI visibility gaps from your traditional vs. Generative AI performance shares.
            </p>
            <div style="flex:1"></div>
            ${BtnPrimary("Start Analysis", { attrs: 'data-act="run-gsc"', style: "justify-content:center;width:100%" })}
            <button data-act="demo-gsc" style="border:none;background:transparent;color:${T.violet};font-family:${FONT};font-size:12px;font-weight:700;cursor:pointer;text-decoration:underline;text-underline-offset:3px;margin-top:10px">Try demo</button>`,
          { style: "padding:22px;display:flex;flex-direction:column" })}

          ${Card(`
            ${Pill(T.magenta, T.magentaSoft, AioLabel("WITH SERP", 10), "align-self:flex-start")}
            <div style="font-size:16.5px;font-weight:800;letter-spacing:-0.3px;margin:10px 0 6px">Run with SERP</div>
            <p style="font-size:12.5px;color:${T.sub};line-height:1.55;margin:0 0 16px">
              Everything in the GSC run, plus a live DataForSEO check of your top 100 keywords: was an AI Overview shown, and is your site cited in it?
            </p>
            ${!dfs ? `<div style="font-size:10.5px;color:${T.muted};margin:-6px 0 16px;text-align:center">Requires a DataForSEO API key — you'll be asked to add one.</div>` : ""}
            <div style="flex:1"></div>
            <button data-act="run-serp" style="display:inline-flex;align-items:center;justify-content:center;gap:7px;background:${T.magenta};color:#fff;border:none;border-radius:11px;padding:11px 20px;font-family:${FONT};font-size:13.5px;font-weight:700;cursor:pointer;width:100%">
              Start Analysis
            </button>
            <button data-act="demo-serp" style="border:none;background:transparent;color:${T.magenta};font-family:${FONT};font-size:12px;font-weight:700;cursor:pointer;text-decoration:underline;text-underline-offset:3px;margin-top:10px">Try demo</button>`,
          { ai: true, style: "padding:22px;display:flex;flex-direction:column" })}
        </div>

        ${SectionTitle("Previous scans", { right: scans.length ? `${scans.length} saved` : "" })}
        ${scans.length === 0
          ? Card(`<div style="font-size:12.5px;color:${T.sub}">No scans yet. Your analyses are saved here so you can reopen them anytime.</div>`, { style: "padding:18px 20px" })
          : `<div style="display:flex;flex-direction:column;gap:8px">
              ${scans.map(sc => Card(`
                <div style="min-width:0">
                  <div style="font-family:${MONO};font-weight:700;font-size:12.5px;color:${T.violet};overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(sc.site)}</div>
                  <div style="font-size:10.5px;color:${T.muted}">${new Date(sc.ts).toLocaleString()} · ${escapeHtml(sc.rangeLabel || "")}</div>
                </div>
                ${sc.hasSerp || sc.mode === "serp"
                  ? Pill(T.magenta, T.magentaSoft, AioLabel("WITH SERP", 10))
                  : Pill(T.violet, T.violetSoft, "GSC ONLY")}
                ${sc.demo ? Pill(T.muted, T.lineSoft, "DEMO") : "<span></span>"}
                <div style="font-family:${MONO};font-size:11.5px;color:${T.sub};text-align:right">${sc.opps != null ? `${sc.opps} opps · ${fmt(sc.oppClicks)} clicks at stake` : escapeHtml(sc.status || "")}</div>
                ${BtnNeutral("Open", { small: true, attrs: `data-act="open" data-id="${sc.id}" data-status="${sc.status}"` })}
                <button data-act="delete" data-id="${sc.id}" title="Delete scan" style="border:none;background:transparent;color:${T.muted};cursor:pointer;font-size:13px;padding:4px">✕</button>`,
              { attrs: `data-act="open" data-id="${sc.id}" data-status="${sc.status}" class="click-card"`, style: `padding:13px 18px;display:grid;grid-template-columns:minmax(0,1.4fr) auto auto minmax(0,1fr) auto auto;align-items:center;gap:14px` })).join("")}
            </div>`}
      </div>
    </div>`;
}

export function wireHome(app, actions) {
  /* one busy flag: while an action runs, further dashboard clicks are ignored —
     no more "clicked 6 times, nothing happened, then 6 scans appeared" */
  let busy = false;
  const setBusy = on => {
    busy = on;
    app.querySelectorAll("button").forEach(b => { b.disabled = on; b.style.opacity = on ? 0.6 : ""; b.style.pointerEvents = on ? "none" : ""; });
  };
  app.querySelectorAll("[data-act]").forEach(el => {
    el.addEventListener("click", async e => {
      e.stopPropagation();
      if (busy) return;
      const act = el.dataset.act;
      const id = el.dataset.id;
      const status = el.dataset.status;
      if (act === "run-gsc") { setBusy(true); actions.onRun("gsc"); return; }
      if (act === "run-serp") { setBusy(true); actions.onRun("serp"); return; }
      if (act === "demo-gsc" || act === "demo-serp") {
        setBusy(true);
        const label = act === "demo-gsc" ? "Starting demo…" : "Starting SERP demo…";
        const btn = el.tagName === "BUTTON" ? el : el.querySelector("button[data-act]");
        if (btn) btn.textContent = label;
        await actions.onDemo(act === "demo-serp");
        return;
      }
      if (act === "open") { setBusy(true); await actions.onOpenScan(id, status); return; }
      if (act === "delete") {
        setBusy(true);
        try { await actions.onDeleteScan(id); } finally { setBusy(false); }
        return;
      }
      if (act === "account") actions.onAccount();
    });
  });
}
